import apiClient from './api-client';

export interface LoanWAL {
  loan_id: number;
  client_loan_number: string;
  loan_name: string;
  wal_years: number;
  remaining_term_years: number;
  average_life_months: number;
  effective_maturity_date: string;
  calculation_date: string;
}

export interface LoanRiskMetrics {
  loan_id: number;
  loan_name: string;
  loan_status: string;
  property_type: string | null;
  sector: string;
  current_balance: number;
  original_balance: number;
  ltv_current: number | null;
  dscr_current: number | null;
  internal_credit_rating: string | null;
  fair_value_clean: number;
  price_clean_pct: number;
  rpx_total_spread_bps: number;
  rpx_base_spread_bps: number;
  market_yield: number;
  wal_years: number;
  modified_duration_years: number;
  effective_maturity_date: string;
  valuation_date: string;
  last_updated: string;
  // Legacy fields for backward compatibility
  client_loan_number?: string;
  property_sector?: string;
  debt_yield_current?: number;
  pd?: number; // Probability of Default
  lgd?: number; // Loss Given Default
  expected_loss?: number;
  risk_rating?: string;
  watchlist_flag?: boolean;
  default_probability_1yr?: number;
  default_probability_lifetime?: number;
}

export interface LoanSpreadBreakdown {
  loan_id: number;
  loan_name?: string;
  client_loan_number?: string;
  property_sector?: string;
  loan_status?: string;
  ltv_current: number;
  dscr_current: number;
  ltv_adjustment_bps: number;
  dscr_adjustment_bps: number;
  base_spread_bps: number;
  total_rpx_adjustment_bps: number;
  effective_spread_bps: number;
}

export interface LoanSpreadBreakdownResponse {
  items: LoanSpreadBreakdown[];
  average_effective_spread_bps: number;
  average_base_spread_bps: number;
  average_total_adjustment_bps: number;
}

export interface LoanRiskMetricsPortfolioSummary {
  total_loans: number;
  total_balance: number;
  weighted_average_ltv: number;
  weighted_average_dscr: number;
  loans_with_ltv: number;
  loans_with_dscr: number;
}

export interface LoanRiskMetricsResponse {
  loans: LoanRiskMetrics[];
  portfolio_summary: LoanRiskMetricsPortfolioSummary;
}

export interface PortfolioRiskSummary {
  total_loans: number;
  total_balance: number;
  average_ltv: number;
  average_dscr: number;
  average_debt_yield: number;
  performing_count: number;
  watchlist_count: number;
  default_count: number;
  weighted_average_pd: number;
  weighted_average_lgd: number;
  total_expected_loss: number;
  risk_distribution: Array<{
    risk_rating: string;
    count: number;
    balance: number;
    percentage: number;
  }>;
}

export const loanAnalyticsService = {
  // Get loan WAL (Weighted Average Life) data
  async getLoanWAL(): Promise<LoanWAL[]> {
    const response = await apiClient.get('/api/v1/valuation/loan-wal');
    return response.data;
  },

  // Get loan risk metrics
  async getLoanRiskMetrics(): Promise<LoanRiskMetricsResponse> {
    const response = await apiClient.get('/api/v1/valuation/risk-metrics');
    return response.data;
  },

  // Get loan spread breakdown
  async getLoanSpreadBreakdown(): Promise<LoanSpreadBreakdownResponse> {
    const response = await apiClient.get(
      '/api/v1/valuation/loan-spread-breakdown'
    );
    return response.data as LoanSpreadBreakdownResponse;
  },

  // Get risk metrics for a specific loan
  async getLoanRiskMetricsById(
    loanId: string
  ): Promise<LoanRiskMetrics | undefined> {
    const response = await this.getLoanRiskMetrics();
    return response.loans.find(
      (m) =>
        m.loan_id.toString() === loanId ||
        m.client_loan_number === loanId ||
        String(m.loan_id) === loanId
    );
  },

  // Calculate portfolio risk summary
  async getPortfolioRiskSummary(): Promise<PortfolioRiskSummary> {
    const response = await this.getLoanRiskMetrics();
    const metrics = response.loans;

    const totalBalance = metrics.reduce((sum, m) => sum + m.current_balance, 0);
    const performingLoans = metrics.filter(
      (m) => m.loan_status === 'Performing'
    );
    const watchlistLoans = metrics.filter((m) => m.watchlist_flag === true);
    const defaultLoans = metrics.filter((m) => m.loan_status === 'Default');

    // Use weighted averages from portfolio_summary instead of calculating
    const weightedLTV = response.portfolio_summary.weighted_average_ltv;
    const weightedDSCR = response.portfolio_summary.weighted_average_dscr;

    const weightedDebtYield =
      totalBalance > 0
        ? metrics.reduce(
            (sum, m) =>
              sum + ((m.debt_yield_current ?? 0) || 0) * m.current_balance,
            0
          ) / totalBalance
        : 0;
    const weightedPD =
      totalBalance > 0
        ? metrics.reduce(
            (sum, m) => sum + ((m.pd ?? 0) || 0) * m.current_balance,
            0
          ) / totalBalance
        : 0;
    const weightedLGD =
      totalBalance > 0
        ? metrics.reduce(
            (sum, m) => sum + ((m.lgd ?? 0) || 0) * m.current_balance,
            0
          ) / totalBalance
        : 0;

    // Group by risk rating (use internal_credit_rating if available, otherwise risk_rating)
    const riskGroups = metrics.reduce(
      (acc, m) => {
        const rating = m.internal_credit_rating || m.risk_rating || 'Unrated';
        if (!acc[rating]) {
          acc[rating] = { count: 0, balance: 0 };
        }
        acc[rating].count++;
        acc[rating].balance += m.current_balance;
        return acc;
      },
      {} as Record<string, { count: number; balance: number }>
    );

    const riskDistribution = Object.entries(riskGroups).map(
      ([rating, data]) => ({
        risk_rating: rating,
        count: data.count,
        balance: data.balance,
        percentage: totalBalance > 0 ? (data.balance / totalBalance) * 100 : 0,
      })
    );

    return {
      total_loans: metrics.length,
      total_balance: totalBalance,
      average_ltv: weightedLTV,
      average_dscr: weightedDSCR,
      average_debt_yield: weightedDebtYield,
      performing_count: performingLoans.length,
      watchlist_count: watchlistLoans.length,
      default_count: defaultLoans.length,
      weighted_average_pd: weightedPD,
      weighted_average_lgd: weightedLGD,
      total_expected_loss: metrics.reduce(
        (sum, m) => sum + ((m.expected_loss ?? 0) || 0),
        0
      ),
      risk_distribution: riskDistribution,
    };
  },
};
